import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgLaborBay, MfgLaborBayKind } from "@/lib/manufacturing/laborBillingClient";
import type { LaborBayDraft } from "./useManufacturingLaborBilling";

function kindLabel(kind: MfgLaborBayKind) {
  return kind === "roofing_all_lines" ? "Roofing (all lines)" : "Builder";
}

export function LaborBayForm({
  title,
  draft,
  setDraft,
  busy,
  onCancel,
  onSubmit,
}: {
  title: string;
  draft: LaborBayDraft;
  setDraft: (next: LaborBayDraft) => void;
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 dark:border-zinc-700 dark:bg-zinc-900/40">
      <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</h4>
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Bay code
          <input
            className={inputClass}
            value={draft.bay_code}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, bay_code: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Name
          <input
            className={inputClass}
            value={draft.name}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400">
          Kind
          <select
            className={inputClass}
            value={draft.kind}
            disabled={busy}
            onChange={(e) =>
              setDraft({ ...draft, kind: e.target.value as MfgLaborBayKind })
            }
          >
            <option value="builder">Builder</option>
            <option value="roofing_all_lines">Roofing (all lines)</option>
          </select>
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-400 sm:col-span-2">
          Notes
          <input
            className={inputClass}
            value={draft.notes}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        </label>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={onSubmit}>
          Save
        </button>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function LaborBaysList({
  bays,
  busy,
  editingBayId,
  onEdit,
  onToggleActive,
  onRemove,
}: {
  bays: MfgLaborBay[];
  busy: boolean;
  editingBayId: string | null;
  onEdit: (bay: MfgLaborBay) => void;
  onToggleActive: (bay: MfgLaborBay) => void;
  onRemove: (bayId: string) => void;
}) {
  if (bays.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">
        No bays yet. Add a builder bay or roofing bay to start.
      </p>
    );
  }

  return (
    <ul className="divide-y divide-gray-200 dark:divide-zinc-700">
      {bays.map((bay) => (
        <li
          key={bay.id}
          className={`flex flex-wrap items-start justify-between gap-2 py-3 ${
            editingBayId === bay.id ? "bg-amber-50/60 dark:bg-amber-950/20" : ""
          }`}
        >
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-900 dark:text-zinc-100">{bay.name}</span>
              <code className="text-xs text-gray-500 dark:text-zinc-400">{bay.bay_code}</code>
              <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-zinc-700 dark:text-zinc-200">
                {kindLabel(bay.kind)}
              </span>
              {!bay.is_active ? (
                <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                  Inactive
                </span>
              ) : null}
            </div>
            {bay.notes ? (
              <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">{bay.notes}</p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              className={ghostBtnClass}
              disabled={busy}
              onClick={() => onEdit(bay)}
            >
              Edit
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={busy}
              onClick={() => onToggleActive(bay)}
            >
              {bay.is_active ? "Deactivate" : "Activate"}
            </button>
            <button
              type="button"
              className={ghostBtnClass}
              disabled={busy}
              onClick={() => onRemove(bay.id)}
            >
              Remove
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
